import { useCallback, useEffect, useState } from "react";
import {
  X,
  RefreshCw,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  PauseCircle,
  Ban,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import AutomationService from "../../services/AutomationService";
import type ExecutionStatus from "../../models/ExecutionStatus";
import type ExecutionLogEvent from "../../models/ExecutionLogEvent";

interface ExecutionHistoryPanelProps {
  workflowId: string;
  /** Mapa id → nombre visible de los nodos del canvas, para etiquetar los eventos. */
  nodeNames: Record<string, string>;
  onClose: () => void;
}

const STATUS_META: Record<string, { label: string; icon: LucideIcon; color: string }> = {
  Pending: { label: "Pendiente", icon: Clock, color: "text-amber-600 bg-amber-500/10 border-amber-500/20" },
  Running: { label: "Ejecutando", icon: Loader2, color: "text-blue-600 bg-blue-500/10 border-blue-500/20" },
  Completed: { label: "Completado", icon: CheckCircle2, color: "text-green-600 bg-green-500/10 border-green-500/20" },
  Failed: { label: "Fallido", icon: XCircle, color: "text-red-600 bg-red-500/10 border-red-500/20" },
  Suspended: { label: "Suspendido", icon: PauseCircle, color: "text-orange-600 bg-orange-500/10 border-orange-500/20" },
  Cancelled: { label: "Cancelado", icon: Ban, color: "text-gray-600 bg-gray-500/10 border-gray-500/20" },
};

const service = new AutomationService();

function isErrorEvent(eventType: string): boolean {
  return /fail|error|invalid/i.test(eventType);
}

function prettyPayload(payloadJson?: string | null): string | null {
  if (!payloadJson) return null;
  try {
    return JSON.stringify(JSON.parse(payloadJson), null, 2);
  } catch {
    return payloadJson;
  }
}

function LogEventRow({ event, nodeNames }: { event: ExecutionLogEvent; nodeNames: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const error = isErrorEvent(event.eventType);
  const payload = prettyPayload(event.payloadJson);

  return (
    <div className={`rounded-lg border ${error ? "border-red-500/20 bg-red-500/5" : "border-black/5 bg-black/[0.02]"}`}>
      <button
        type="button"
        onClick={() => payload && setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left ${payload ? "cursor-pointer" : "cursor-default"}`}
      >
        {payload ? (
          open ? <ChevronDown className="size-3 shrink-0 text-foreground/30" /> : <ChevronRight className="size-3 shrink-0 text-foreground/30" />
        ) : (
          <span className="size-3 shrink-0" />
        )}
        <span className={`text-[11px] font-mono font-semibold ${error ? "text-red-600" : "text-foreground/70"}`}>
          {event.eventType}
        </span>
        {event.nodeId && (
          <span className="text-[11px] text-foreground/40 truncate">
            {nodeNames[event.nodeId] ?? event.nodeId}
          </span>
        )}
        <span className="ml-auto text-[10px] text-foreground/30 font-mono shrink-0">
          {new Date(event.timestampUtc).toLocaleTimeString()}
        </span>
      </button>
      {open && payload && (
        <pre className="text-[10px] bg-gray-900 text-gray-100 rounded-b-lg px-3 py-2 font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-64 overflow-y-auto">
          {payload}
        </pre>
      )}
    </div>
  );
}

export default function ExecutionHistoryPanel({ workflowId, nodeNames, onClose }: ExecutionHistoryPanelProps) {
  const [executions, setExecutions] = useState<ExecutionStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [logs, setLogs] = useState<ExecutionLogEvent[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadExecutions = useCallback(async () => {
    setLoading(true);
    try {
      setExecutions(await service.GetExecutions(workflowId));
    } catch {
      // error handled by ApiAcces interceptor
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    void loadExecutions();
  }, [loadExecutions]);

  const selectExecution = useCallback(
    async (executionId: string) => {
      if (selectedId === executionId) {
        setSelectedId(null);
        setLogs([]);
        return;
      }
      setSelectedId(executionId);
      setLogsLoading(true);
      setLogs([]);
      try {
        setLogs(await service.GetExecutionLogs(workflowId, executionId));
      } catch {
        // error handled by ApiAcces interceptor
      } finally {
        setLogsLoading(false);
      }
    },
    [workflowId, selectedId],
  );

  return (
    <aside className="w-[28rem] shrink-0 border-l border-black/5 bg-white/70 backdrop-blur-md overflow-y-auto">
      <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-3.5 bg-white/85 backdrop-blur-md border-b border-black/5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent shadow-sm">
          <Clock className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-foreground truncate">Historial de ejecuciones</h2>
          <p className="text-[11px] text-foreground/40 font-medium truncate">
            {executions.length} {executions.length === 1 ? "ejecución reciente" : "ejecuciones recientes"}
          </p>
        </div>
        <button
          onClick={() => void loadExecutions()}
          className="flex size-8 shrink-0 items-center justify-center rounded-xl text-foreground/40 hover:bg-black/5 hover:text-foreground transition-colors cursor-pointer"
          aria-label="Refrescar"
          title="Refrescar"
        >
          <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        <button
          onClick={onClose}
          className="flex size-8 shrink-0 items-center justify-center rounded-xl text-foreground/40 hover:bg-black/5 hover:text-foreground transition-colors cursor-pointer"
          aria-label="Cerrar"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="p-5 space-y-4">
      {executions.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <span className="flex size-12 items-center justify-center rounded-2xl bg-black/5 mb-3">
            <Clock className="size-6 text-foreground/25" />
          </span>
          <p className="text-sm text-foreground/40">Aún no hay ejecuciones de esta automatización.</p>
        </div>
      )}

      <div className="space-y-2">
        {executions.map((execution) => {
          const meta = STATUS_META[execution.status] ?? STATUS_META.Pending;
          const Icon = meta.icon;
          const selected = selectedId === execution.executionId;
          return (
            <div key={execution.executionId} className="rounded-xl border border-black/5 bg-white/60 overflow-hidden">
              <button
                type="button"
                onClick={() => void selectExecution(execution.executionId)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-black/[0.02] transition-colors"
              >
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-bold shrink-0 ${meta.color}`}>
                  <Icon className="size-3" />
                  {meta.label}
                </span>
                <span className="text-[11px] text-foreground/50 truncate font-mono">
                  {execution.executionId.slice(0, 8)}
                </span>
                <span className="ml-auto text-[11px] text-foreground/40 shrink-0">
                  {new Date(execution.enqueuedAtUtc).toLocaleString()}
                </span>
              </button>

              {selected && (
                <div className="border-t border-black/5 p-2.5 space-y-1.5 bg-black/[0.015]">
                  {logsLoading ? (
                    <p className="text-[11px] text-foreground/40 flex items-center gap-1.5 px-1">
                      <Loader2 className="size-3 animate-spin" />
                      Cargando logs…
                    </p>
                  ) : logs.length === 0 ? (
                    <p className="text-[11px] text-foreground/40 px-1">Sin eventos registrados.</p>
                  ) : (
                    logs.map((event, i) => (
                      <LogEventRow key={i} event={event} nodeNames={nodeNames} />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </aside>
  );
}
